/**
 * Recovery Plan Executor
 *
 * Executes AI-generated recovery plans with one-click implementation.
 * Handles atomic execution with transaction support and rollback capability.
 * Provides undo functionality for 24 hours after execution.
 */

import { createClient } from '@supabase/supabase-js';
import { addDays, format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

export class RecoveryPlanExecutor {
  constructor(supabase) {
    // Accept supabase client as parameter, or create one if not provided
    if (supabase) {
      this.supabase = supabase;
    } else {
      // Fallback to creating client (for backwards compatibility)
      const url = process.env.REACT_APP_SUPABASE_URL || process.env.VITE_SUPABASE_URL;
      const key = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY;

      if (!url || !key) {
        console.error('Supabase credentials not found in environment variables');
        // Create a dummy client to prevent crashes
        this.supabase = null;
      } else {
        this.supabase = createClient(url, key);
      }
    }
    this.undoDataExpiration = 24; // hours
  }

  /**
   * Present recovery plan to user with preview
   * @param {Object} apiResponse - Structured response from Claude API
   * @param {Array} currentTasks - Current timeline tasks
   * @returns {Object} Presentation object with preview and actions
   */
  async presentPlan(apiResponse, currentTasks) {
    // Validate the response structure
    const validated = this.validateResponse(apiResponse);

    // Generate visual preview of changes
    const preview = await this.generateVisualPreview(validated.actions, currentTasks);

    // Prepare execution options
    return {
      explanation: validated.explanation,
      preview: preview,

      // Primary action - one-click execution
      executeButton: {
        label: "Apply This Plan",
        action: () => this.executePlan(validated.actions, currentTasks),
        confidence: validated.metadata.confidence,
        estimatedTime: "~5 seconds",
        className: "btn-primary"
      },

      // Customization option
      customizeButton: {
        label: "Customize First",
        action: () => this.showCustomizationUI(validated.actions, currentTasks),
        className: "btn-secondary"
      },

      // Manual option
      manualButton: {
        label: "I'll Handle It Myself",
        action: () => this.dismissWithTracking('manual'),
        className: "btn-tertiary"
      },

      // Metadata
      metadata: {
        ...validated.metadata,
        taskCount: validated.actions.taskUpdates.length,
        batchCount: validated.actions.batchCreation.length,
        estimatedTimeSaved: validated.metadata.time_saved_hours
      }
    };
  }

  /**
   * Execute the recovery plan with transaction support
   * @param {Object} actions - Actions to execute
   * @param {Array} currentTasks - Current timeline tasks for reference
   * @returns {Object} Execution result with undo token
   */
  async executePlan(actions, currentTasks) {
    console.log('[RecoveryPlanExecutor] Starting plan execution');

    // Transform actions to expected format if needed
    const transformedActions = Array.isArray(actions)
      ? this.transformActionsForPreview(actions)
      : actions;

    // Ensure required properties exist
    const safeActions = {
      taskUpdates: transformedActions.taskUpdates || [],
      batchCreation: transformedActions.batchCreation || [],
      schoolUpdates: transformedActions.schoolUpdates || [],
      timelineAdjustments: transformedActions.timelineAdjustments || {}
    };

    // Store original state for potential undo (simplified approach)
    const originalState = {
      timestamp: new Date(),
      tasks: currentTasks.map(t => ({ ...t }))
    };

    const results = {
      succeeded: [],
      failed: [],
      skipped: []
    };

    try {
      // Phase 1: Execute task updates (reschedule, delete, mark optional)
      console.log(`[RecoveryPlanExecutor] Phase 1: Processing ${safeActions.taskUpdates.length} task updates`);

      for (const update of safeActions.taskUpdates) {
        try {
          const result = await this.executeTaskUpdate(update);
          results.succeeded.push({
            type: 'task_update',
            ...update,
            result
          });
        } catch (error) {
          console.error(`[RecoveryPlanExecutor] Failed to update task ${update.id}:`, error);
          results.failed.push({
            type: 'task_update',
            update,
            error: error.message
          });
        }
      }

      // Phase 2: Create batches
      console.log(`[RecoveryPlanExecutor] Phase 2: Creating ${safeActions.batchCreation.length} batches`);

      for (const batch of safeActions.batchCreation) {
        try {
          const batchId = await this.createBatch(batch, currentTasks);
          results.succeeded.push({
            type: 'batch_creation',
            ...batch,
            createdId: batchId
          });
        } catch (error) {
          console.error(`[RecoveryPlanExecutor] Failed to create batch ${batch.title}:`, error);
          results.failed.push({
            type: 'batch_creation',
            batch,
            error: error.message
          });
        }
      }

      // Phase 3: Apply smart school categorization (EA/ED/RD strategy)
      if (safeActions.schoolUpdates && safeActions.schoolUpdates.length > 0) {
        console.log(`[RecoveryPlanExecutor] Phase 3: Applying smart school categorization to ${safeActions.schoolUpdates.length} schools`);

        for (const schoolUpdate of safeActions.schoolUpdates) {
          try {
            await this.updateSchoolApplicationRound(schoolUpdate);
            results.succeeded.push({
              type: 'school_update',
              ...schoolUpdate
            });
          } catch (error) {
            console.error(`[RecoveryPlanExecutor] Failed to update school ${schoolUpdate.schoolName}:`, error);
            results.failed.push({
              type: 'school_update',
              schoolUpdate,
              error: error.message
            });
          }
        }
      }

      // Phase 4: Apply timeline-wide adjustments
      if (safeActions.timelineAdjustments && Object.keys(safeActions.timelineAdjustments).length > 0) {
        console.log('[RecoveryPlanExecutor] Phase 4: Applying timeline adjustments');

        try {
          await this.applyTimelineAdjustments(safeActions.timelineAdjustments);
          results.succeeded.push({
            type: 'timeline_adjustment',
            details: safeActions.timelineAdjustments
          });
        } catch (error) {
          console.error('[RecoveryPlanExecutor] Failed to apply timeline adjustments:', error);
          results.failed.push({
            type: 'timeline_adjustment',
            error: error.message
          });
        }
      }

      // Evaluate success and decide whether to commit
      const successRate = results.succeeded.length /
        (results.succeeded.length + results.failed.length + results.skipped.length);

      if (successRate > 0.8 && results.succeeded.length > 0) {
        // Store undo information
        const undoToken = await this.storeUndoData({
          original: originalState,
          changes: results.succeeded,
          timestamp: new Date(),
          expires: addDays(new Date(), 1)
        });

        console.log('[RecoveryPlanExecutor] Plan executed successfully');

        return {
          success: true,
          results,
          undoToken,
          message: this.generateSuccessMessage(results),
          summary: {
            tasksUpdated: results.succeeded.filter(r => r.type === 'task_update').length,
            batchesCreated: results.succeeded.filter(r => r.type === 'batch_creation').length,
            adjustmentsApplied: results.succeeded.filter(r => r.type === 'timeline_adjustment').length
          }
        };
      } else {
        console.warn('[RecoveryPlanExecutor] Too many failures');

        return {
          success: false,
          results,
          message: 'Too many operations failed. Some changes may have been applied.',
          details: this.generateFailureDetails(results)
        };
      }

    } catch (criticalError) {
      // Critical failure
      console.error('[RecoveryPlanExecutor] Critical failure:', criticalError);

      throw new Error(`Critical failure in plan execution: ${criticalError.message}`);
    }
  }

  /**
   * Execute individual task update
   */
  async executeTaskUpdate(update) {
    const { id, action, from, to, newEstimatedHours, reason } = update;

    console.log(`[RecoveryPlanExecutor] Executing ${action} on task ${id}`);

    // For now, use direct Supabase calls instead of transaction methods
    // TODO: Implement proper transactional database operations

    switch (action) {
      case 'reschedule':
        // First, try to find the task by title if id is not a UUID
        let taskId = id;
        if (!id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          const { data: task } = await this.supabase
            .from('timeline_items')
            .select('id')
            .eq('title', id)
            .single();

          if (task) taskId = task.id;
          else {
            console.warn(`[RecoveryPlanExecutor] Task not found by title: ${id}`);
            return { skipped: true, reason: 'Task not found' };
          }
        }

        const { error: rescheduleError } = await this.supabase
          .from('timeline_items')
          .update({
            scheduled_date: to,
            notes: `Rescheduled from ${from}: ${reason}`,
            updated_at: new Date().toISOString()
          })
          .eq('id', taskId);

        if (rescheduleError) throw rescheduleError;
        return { updated: true };

      case 'delete':
        // First, try to find the task by title if id is not a UUID
        let deleteTaskId = id;
        if (!id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          const { data: task } = await this.supabase
            .from('timeline_items')
            .select('id')
            .eq('title', id)
            .single();

          if (task) deleteTaskId = task.id;
          else {
            console.warn(`[RecoveryPlanExecutor] Task not found by title: ${id}`);
            return { skipped: true, reason: 'Task not found' };
          }
        }

        // Soft delete using status field
        const { error: deleteError } = await this.supabase
          .from('timeline_items')
          .update({
            status: 'cancelled',
            notes: `Cancelled by recovery plan: ${reason}`,
            updated_at: new Date().toISOString()
          })
          .eq('id', deleteTaskId);

        if (deleteError) throw deleteError;
        return { deleted: true };

      case 'mark_optional':
        // First, try to find the task by title if id is not a UUID
        let optionalTaskId = id;
        if (!id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          const { data: task } = await this.supabase
            .from('timeline_items')
            .select('id')
            .eq('title', id)
            .single();

          if (task) optionalTaskId = task.id;
          else {
            console.warn(`[RecoveryPlanExecutor] Task not found by title: ${id}`);
            return { skipped: true, reason: 'Task not found' };
          }
        }

        const { error: optionalError } = await this.supabase
          .from('timeline_items')
          .update({
            priority: 'low',
            metadata: {
              marked_optional: true,
              optional_reason: reason,
              marked_via: 'ai_recovery_plan'
            },
            updated_at: new Date().toISOString()
          })
          .eq('id', optionalTaskId);

        if (optionalError) throw optionalError;
        return { marked_optional: true };

      case 'modify':
        // First, try to find the task by title if id is not a UUID
        let modifyTaskId = id;
        if (!id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          const { data: task } = await this.supabase
            .from('timeline_items')
            .select('id')
            .eq('title', id)
            .single();

          if (task) modifyTaskId = task.id;
          else {
            console.warn(`[RecoveryPlanExecutor] Task not found by title: ${id}`);
            return { skipped: true, reason: 'Task not found' };
          }
        }

        const { error: modifyError } = await this.supabase
          .from('timeline_items')
          .update({
            estimated_hours: newEstimatedHours,
            metadata: {
              hours_modified: true,
              original_hours: from,
              modification_reason: reason
            },
            updated_at: new Date().toISOString()
          })
          .eq('id', modifyTaskId);

        if (modifyError) throw modifyError;
        return { modified: true };

      case 'focus':
      case 'prioritize':  // Handle prioritize same as focus
        // First, try to find the task by title if id is not a UUID
        let focusTaskId = id;
        if (!id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          const { data: task } = await this.supabase
            .from('timeline_items')
            .select('id')
            .eq('title', id)
            .single();

          if (task) focusTaskId = task.id;
          else {
            console.warn(`[RecoveryPlanExecutor] Task not found by title: ${id}`);
            return { skipped: true, reason: 'Task not found' };
          }
        }

        const { error: focusError } = await this.supabase
          .from('timeline_items')
          .update({
            priority: 'high',
            metadata: {
              focused: true,
              focus_reason: reason,
              marked_via: 'ai_recovery_plan'
            },
            updated_at: new Date().toISOString()
          })
          .eq('id', focusTaskId);

        if (focusError) throw focusError;
        return { focused: true };

      case 'defer':
        // Handle defer - push task out by 2 weeks
        let deferTaskId = id;
        if (!id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          const { data: task } = await this.supabase
            .from('timeline_items')
            .select('id, scheduled_date')
            .eq('title', id)
            .single();

          if (task) {
            deferTaskId = task.id;
            const currentDate = new Date(task.scheduled_date || new Date());
            const deferredDate = new Date(currentDate);
            deferredDate.setDate(deferredDate.getDate() + 14); // Defer by 2 weeks

            const { error: deferError } = await this.supabase
              .from('timeline_items')
              .update({
                scheduled_date: deferredDate.toISOString().split('T')[0],
                priority: 'low',
                notes: `Deferred by recovery plan: ${reason}`,
                updated_at: new Date().toISOString()
              })
              .eq('id', deferTaskId);

            if (deferError) throw deferError;
            return { deferred: true };
          } else {
            console.warn(`[RecoveryPlanExecutor] Task not found by title: ${id}`);
            return { skipped: true, reason: 'Task not found' };
          }
        }

      case 'batch':
        // Handle batch - mark as part of a batch group
        // This is more complex and would need batch creation logic
        console.warn(`[RecoveryPlanExecutor] Batch action not yet fully implemented`);
        return { skipped: true, reason: 'Batch action pending implementation' };

      default:
        throw new Error(`Unknown action type: ${action}`);
    }
  }

  /**
   * Create a batch of tasks
   */
  async createBatch(batchConfig, currentTasks) {
    const batchId = `batch_${uuidv4()}`;

    // Prepare batch record
    const batch = {
      id: batchId,
      title: batchConfig.title,
      type: 'intelligent_batch',
      scheduled_date: batchConfig.scheduled_date,
      duration_hours: batchConfig.duration_hours,
      batch_type: batchConfig.type,
      instructions: batchConfig.instructions,
      synergy_score: batchConfig.synergy_score || 0.8,
      created_by: 'ai_recovery_plan',
      created_at: new Date().toISOString(),
      metadata: {
        task_ids: batchConfig.tasks,
        break_pattern: batchConfig.break_pattern,
        original_total_hours: this.calculateOriginalHours(batchConfig.tasks, currentTasks)
      }
    };

    // Create batch as a timeline_item using valid schema fields
    const { error: batchError } = await this.supabase
      .from('timeline_items')
      .insert({
        id: batchId,
        user_id: currentTasks[0]?.user_id, // Get user_id from existing tasks
        title: batch.title,
        description: batch.instructions,
        source_type: 'system_generated',
        scheduled_date: batch.scheduled_date,
        estimated_duration: Math.round(batch.duration_hours * 60), // Convert hours to minutes
        // Store batch-specific data in metadata field since those columns don't exist
        metadata: {
          ...batch.metadata,
          is_batch: true,
          batch_items: batchConfig.tasks,
          batch_type: batch.batch_type,
          synergy_score: batch.synergy_score,
          duration_hours: batch.duration_hours
        },
        status: 'planned',
        priority: 'medium',
        category: 'batch',
        created_at: batch.created_at,
        updated_at: batch.created_at
      });

    if (batchError) {
      console.warn(`[RecoveryPlanExecutor] Failed to create batch, skipping:`, batchError);
      return null; // Skip batch creation if it fails
    }

    // Update tasks to reference the batch
    for (const taskId of batchConfig.tasks) {
      const { error: taskError } = await this.supabase
        .from('timeline_items')
        .update({
          batch_id: batchId,
          scheduled_date: batchConfig.scheduled_date,
          metadata: {
            batched: true,
            batch_title: batchConfig.title
          }
        })
        .eq('id', taskId);

      if (taskError) {
        console.warn(`[RecoveryPlanExecutor] Failed to update task ${taskId} for batching:`, taskError);
      }
    }

    console.log(`[RecoveryPlanExecutor] Created batch ${batchId} with ${batchConfig.tasks.length} tasks`);

    return batchId;
  }

  /**
   * Apply timeline-wide adjustments
   */
  async applyTimelineAdjustments(adjustments) {
    // Handle shifting non-critical tasks
    if (adjustments.shift_non_critical) {
      const { days, affected_tasks } = adjustments.shift_non_critical;

      console.log(`[RecoveryPlanExecutor] Shifting ${affected_tasks.length} tasks by ${days} days`);

      for (const taskId of affected_tasks) {
        // Get current task date
        const { data: task } = await this.supabase
          .from('timeline_items')
          .select('scheduled_date, deadline')
          .eq('id', taskId)
          .single();

        if (task) {
          const currentDate = new Date(task.scheduled_date || task.deadline);
          const newDate = addDays(currentDate, days);

          const { error: shiftError } = await this.supabase
            .from('timeline_items')
            .update({
              scheduled_date: format(newDate, 'yyyy-MM-dd'),
              metadata: {
                bulk_shifted: true,
                shift_days: days,
                shift_reason: 'recovery_plan_redistribution'
              }
            })
            .eq('id', taskId);

          if (shiftError) {
            console.warn(`[RecoveryPlanExecutor] Failed to shift task ${taskId}:`, shiftError);
          }
        }
      }
    }

    // Handle creating buffer time
    if (adjustments.create_buffer_time) {
      const { dates, hours_per_day } = adjustments.create_buffer_time;

      for (const date of dates) {
        // Create a buffer task as a visual reminder
        const { error: bufferError } = await this.supabase
          .from('timeline_items')
          .insert({
            id: `buffer_${uuidv4()}`,
            title: 'Buffer Time (Recovery Plan)',
            type: 'buffer',
            scheduled_date: date,
            estimated_hours: hours_per_day,
            priority: 'low',
            category: 'admin',
            metadata: {
              is_buffer: true,
              created_by: 'recovery_plan'
            }
          });

        if (bufferError) {
          console.warn(`[RecoveryPlanExecutor] Failed to create buffer time for ${date}:`, bufferError);
        }
      }
    }
  }

  /**
   * Execute a single action from the recovery plan
   * Used for selective execution in the preview flow
   */
  async executeSingleAction(action, tasks, context) {
    try {
      // Get task ID if it's a title
      let taskId = action.taskId;
      if (!taskId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        const matchingTask = tasks.find(t => t.title === taskId);
        if (matchingTask) {
          taskId = matchingTask.id;
        } else {
          const { data: task, error } = await this.supabase
            .from('timeline_items')
            .select('id')
            .eq('title', taskId)
            .single();

          if (error || !task) {
            throw new Error(`Task not found: ${taskId}`);
          }
          taskId = task.id;
        }
      }

      let result;
      switch (action.type) {
        case 'reschedule':
          result = await this.supabase
            .from('timeline_items')
            .update({
              scheduled_date: action.to,
              notes: `Previous date: ${action.from || 'unscheduled'}. ${action.reason || ''}`
            })
            .eq('id', taskId);
          break;

        case 'defer':
          const deferDate = action.to || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
          result = await this.supabase
            .from('timeline_items')
            .update({
              scheduled_date: deferDate,
              notes: `Deferred from ${action.from || 'current date'}. ${action.reason || ''}`
            })
            .eq('id', taskId);
          break;

        case 'delete':
          result = await this.supabase
            .from('timeline_items')
            .update({
              status: 'cancelled',
              notes: `Removed during crisis recovery. ${action.reason || ''}`
            })
            .eq('id', taskId);
          break;

        case 'focus':
          result = await this.supabase
            .from('timeline_items')
            .update({
              is_priority: true,
              notes: `Marked as priority focus. ${action.reason || ''}`
            })
            .eq('id', taskId);
          break;

        case 'batch':
          // For batching, we'd need to create a new batch task
          // This is more complex and would require additional logic
          result = { error: null };
          console.log(`Batching tasks: ${action.taskIds}`);
          break;

        case 'mark_optional':
          result = await this.supabase
            .from('timeline_items')
            .update({
              is_optional: true,
              notes: `Marked as optional. ${action.reason || ''}`
            })
            .eq('id', taskId);
          break;

        default:
          throw new Error(`Unknown action type: ${action.type}`);
      }

      if (result.error) {
        throw result.error;
      }

      return {
        success: true,
        message: `${action.type} completed for ${action.taskId}`
      };
    } catch (error) {
      console.error(`[RecoveryPlanExecutor] Failed to execute action:`, error);
      throw error;
    }
  }

  /**
   * Store undo data for rollback capability
   */
  async storeUndoData(undoInfo) {
    const undoToken = uuidv4();

    // TODO: Implement undo_history table in Supabase
    // For now, store undo data in localStorage as fallback
    try {
      const undoData = {
        id: undoToken,
        type: 'recovery_plan',
        original_state: undoInfo.original,
        changes: undoInfo.changes,
        created_at: undoInfo.timestamp,
        expires_at: undoInfo.expires,
        used: false
      };

      // Try localStorage first as fallback
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(`undo_${undoToken}`, JSON.stringify(undoData));
        console.log('[RecoveryPlanExecutor] Undo data stored in localStorage (undo_history table not available)');
      }
    } catch (e) {
      console.warn('[RecoveryPlanExecutor] Could not store undo data:', e);
      // Non-critical - plan still succeeded
    }

    return undoToken;
  }

  /**
   * Undo a previously executed plan
   */
  async undoPlan(undoToken) {
    console.log(`[RecoveryPlanExecutor] Attempting to undo plan with token ${undoToken}`);

    // Fetch undo data
    const { data: undoData, error: fetchError } = await this.supabase
      .from('undo_history')
      .select('*')
      .eq('id', undoToken)
      .single();

    if (fetchError || !undoData) {
      throw new Error('Undo data not found or expired');
    }

    if (undoData.used) {
      throw new Error('This undo token has already been used');
    }

    if (new Date() > new Date(undoData.expires_at)) {
      throw new Error('Undo period has expired');
    }

    // Execute rollback
    const transaction = await this.transactionalUpdater.beginTransaction();

    try {
      await this.transactionalUpdater.restoreFromBackup(
        undoData.original_state,
        transaction
      );

      await transaction.commit();

      // Mark undo data as used
      await this.supabase
        .from('undo_history')
        .update({ used: true, used_at: new Date() })
        .eq('id', undoToken);

      console.log('[RecoveryPlanExecutor] Undo completed successfully');

      return {
        success: true,
        message: 'Changes reverted successfully',
        restoredCount: undoData.changes.length
      };
    } catch (error) {
      await transaction.rollback();
      throw new Error(`Failed to undo changes: ${error.message}`);
    }
  }

  /**
   * Generate preview of changes (alias for generateVisualPreview)
   * Used by CrisisSupport component
   */
  async generatePreview(actions, currentTasks) {
    // Transform flat API actions array to expected format
    const transformedActions = this.transformActionsForPreview(actions);
    return this.generateVisualPreview(transformedActions, currentTasks);
  }

  /**
   * Transform flat API actions array to categorized format
   */
  transformActionsForPreview(actions) {
    if (!actions || !Array.isArray(actions)) {
      return { taskUpdates: [], batchCreation: [], timelineAdjustments: [] };
    }

    const taskUpdates = [];
    const batchCreation = [];
    const timelineAdjustments = [];

    actions.forEach(action => {
      // Handle both API response formats (changeType vs type)
      const actionType = action.changeType || action.type;
      const taskId = action.taskId || action.id;
      const fromDate = action.currentDate || action.from;
      const toDate = action.proposedDate || action.to;
      const reason = action.reason || action.impact || 'Schedule optimization';

      if (actionType === 'batch') {
        // Batch multiple tasks together
        batchCreation.push({
          title: reason || 'Batched tasks',
          tasks: [taskId], // In real implementation, might have multiple IDs
          scheduled_date: toDate,
          estimated_hours: 2 // Default estimate
        });
      } else if (actionType === 'timeline_shift') {
        // Global timeline adjustment
        timelineAdjustments.push({
          type: 'shift',
          days: action.days || 7,
          reason: reason
        });
      } else {
        // Standard task update (reschedule, delete, mark_optional, focus)
        taskUpdates.push({
          id: taskId,
          action: actionType || 'reschedule', // Default to reschedule if no type
          from: fromDate,
          to: toDate,
          reason: reason,
          description: `${action.currentTitle || taskId}: ${reason}`,
          deadline_type: action.deadline_type // ED, EA, RD, etc.
        });
      }
    });

    return { taskUpdates, batchCreation, timelineAdjustments };
  }

  /**
   * Generate visual preview of changes
   */
  async generateVisualPreview(actions, currentTasks) {
    const taskMap = new Map(currentTasks.map(t => [t.id, t]));

    return {
      taskUpdates: actions.taskUpdates.map(update => ({
        ...update,
        task: taskMap.get(update.id),
        preview: this.generateUpdatePreview(update, taskMap.get(update.id))
      })),

      batchCreation: actions.batchCreation.map(batch => ({
        ...batch,
        tasks: batch.tasks.map(id => taskMap.get(id)),
        preview: this.generateBatchPreview(batch, taskMap)
      })),

      timelineAdjustments: this.generateAdjustmentPreview(actions.timelineAdjustments, taskMap),

      summary: this.generatePreviewSummary(actions, taskMap)
    };
  }

  /**
   * Generate preview for a single task update
   */
  generateUpdatePreview(update, task) {
    if (!task) return null;

    switch (update.action) {
      case 'reschedule':
        return {
          type: 'reschedule',
          from: update.from,
          to: update.to,
          description: `Move "${task.title}" from ${update.from} to ${update.to}`
        };

      case 'delete':
        return {
          type: 'delete',
          description: `Remove "${task.title}" (${task.estimated_hours}h saved)`
        };

      case 'mark_optional':
        return {
          type: 'optional',
          description: `Mark "${task.title}" as optional`
        };

      default:
        return null;
    }
  }

  /**
   * Generate preview for batch creation
   */
  generateBatchPreview(batch, taskMap) {
    const tasks = batch.tasks.map(id => taskMap.get(id)).filter(Boolean);
    const totalHours = tasks.reduce((sum, t) => sum + (t.estimated_hours || 0.75), 0);

    return {
      type: 'batch',
      title: batch.title,
      taskCount: tasks.length,
      totalHours,
      estimatedSavings: totalHours * 0.25,
      description: `Batch ${tasks.length} tasks into "${batch.title}" session`
    };
  }

  /**
   * Generate preview for timeline adjustments
   */
  generateAdjustmentPreview(adjustments, _taskMap) {
    const previews = [];

    if (adjustments?.shift_non_critical) {
      const { days, affected_tasks } = adjustments.shift_non_critical;
      previews.push({
        type: 'bulk_shift',
        taskCount: affected_tasks.length,
        days,
        description: `Move ${affected_tasks.length} non-critical tasks ${days} days later`
      });
    }

    if (adjustments?.create_buffer_time) {
      const { dates, hours_per_day } = adjustments.create_buffer_time;
      previews.push({
        type: 'buffer_creation',
        dates: dates.length,
        hoursPerDay: hours_per_day,
        description: `Add ${hours_per_day}h buffer time on ${dates.length} days`
      });
    }

    return previews;
  }

  /**
   * Generate preview summary
   */
  generatePreviewSummary(actions, taskMap) {
    const deletedHours = actions.taskUpdates
      .filter(u => u.action === 'delete')
      .reduce((sum, u) => {
        const task = taskMap.get(u.id);
        return sum + (task?.estimated_hours || 0);
      }, 0);

    const batchedHours = actions.batchCreation
      .reduce((sum, batch) => {
        return sum + batch.tasks.reduce((s, id) => {
          const task = taskMap.get(id);
          return s + (task?.estimated_hours || 0);
        }, 0);
      }, 0);

    return {
      totalChanges: actions.taskUpdates.length,
      tasksDeleted: actions.taskUpdates.filter(u => u.action === 'delete').length,
      tasksRescheduled: actions.taskUpdates.filter(u => u.action === 'reschedule').length,
      batchesCreated: actions.batchCreation.length,
      hoursFreed: deletedHours,
      hoursBatched: batchedHours,
      estimatedTimeSaved: deletedHours + (batchedHours * 0.25)
    };
  }

  /**
   * Validate response structure
   */
  validateResponse(response) {
    if (!response.explanation || !response.actions) {
      throw new Error('Invalid response structure: missing required fields');
    }

    // Ensure arrays exist
    response.actions.taskUpdates = response.actions.taskUpdates || [];
    response.actions.batchCreation = response.actions.batchCreation || [];
    response.actions.timelineAdjustments = response.actions.timelineAdjustments || {};

    // Validate metadata
    response.metadata = response.metadata || {
      confidence: 0.8,
      reversible: true
    };

    return response;
  }

  /**
   * Calculate original hours for batched tasks
   */
  calculateOriginalHours(taskIds, currentTasks) {
    const taskMap = new Map(currentTasks.map(t => [t.id, t]));
    return taskIds.reduce((sum, id) => {
      const task = taskMap.get(id);
      return sum + (task?.estimated_hours || 0.75);
    }, 0);
  }

  /**
   * Generate success message
   */
  generateSuccessMessage(results) {
    const succeeded = results.succeeded.length;
    const failed = results.failed.length;

    if (failed === 0) {
      return `Perfect! All ${succeeded} changes applied successfully.`;
    } else {
      return `Recovery plan applied with ${succeeded} successful changes. ${failed} operations could not be completed.`;
    }
  }

  /**
   * Generate failure details
   */
  generateFailureDetails(results) {
    return {
      succeeded: results.succeeded.length,
      failed: results.failed.length,
      failures: results.failed.map(f => ({
        type: f.type,
        error: f.error
      }))
    };
  }

  /**
   * Show customization UI (placeholder)
   */
  showCustomizationUI(actions, currentTasks) {
    // This would open a UI for customizing the plan
    console.log('[RecoveryPlanExecutor] Customization UI requested');
    return {
      actions,
      currentTasks,
      message: 'Customization UI to be implemented'
    };
  }

  /**
   * Dismiss plan with tracking
   */
  dismissWithTracking(reason) {
    console.log(`[RecoveryPlanExecutor] Plan dismissed: ${reason}`);
    // Track this for analytics
    return {
      dismissed: true,
      reason
    };
  }

  /**
   * Update school application round based on smart EA/ED/RD strategy
   */
  async updateSchoolApplicationRound(schoolUpdate) {
    const { schoolId, schoolName, currentCategory, newApplicationRound, reason } = schoolUpdate;

    console.log(`[RecoveryPlanExecutor] Updating ${schoolName} from ${currentCategory} to ${newApplicationRound}: ${reason}`);

    // Update the college record in the database
    const { error } = await this.supabase
      .from('colleges')
      .update({
        application_round: newApplicationRound,
        updated_at: new Date().toISOString()
      })
      .eq('id', schoolId);

    if (error) {
      console.error(`Failed to update school ${schoolName}:`, error);
      throw new Error(`Failed to update ${schoolName}: ${error.message}`);
    }

    return {
      schoolId,
      schoolName,
      previousRound: 'not_set',
      newRound: newApplicationRound,
      reason
    };
  }
}